import { useLocale } from '@solinkcorp/locale';
import { Link2Off } from 'lucide-react';
import React from 'react';
import { type MissingJourneyZones } from './getMissingJourneyZones';

interface JourneyZoneErrorProps {
  /** Which zone(s) the site is missing. `null` renders nothing. */
  missing: MissingJourneyZones;
}

/**
 * Replaces the value of a total-journey-time widget when the site's start/end zone pairing
 * is incomplete, so the widget explains why it has no number instead of showing a misleading
 * zero.
 *
 * Shown on the card in the layout view only — the config modal no longer carries this error,
 * and `JourneyWidget` suppresses it while the layout is being edited.
 */
export const JourneyZoneError: React.FC<JourneyZoneErrorProps> = ({ missing }) => {
  const locale = useLocale();
  if (!missing) return null;

  const copy = {
    both: {
      headline: locale.serviceDisplayMonitor.startAndEndZonesNotAssigned,
      detail: locale.serviceDisplayMonitor.assignZoneTagsToContinue,
    },
    start: {
      headline: locale.serviceDisplayMonitor.startZoneNotAssigned,
      detail: locale.serviceDisplayMonitor.assignStartZoneTagToContinue,
    },
    end: {
      headline: locale.serviceDisplayMonitor.endZoneNotAssigned,
      detail: locale.serviceDisplayMonitor.assignEndZoneTagToContinue,
    },
  }[missing];

  return (
    // Fills the card's value area and centres on both axes, so the error sits where the
    // value would — `WidgetCard` gives that area a height but no centring of its own, the
    // same way `BigValue` fills and centres itself.
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center text-text-error"
      data-testid="journey-zone-error"
    >
      <Link2Off className="size-6 shrink-0" aria-hidden />
      <div className="flex flex-col">
        <p className="m-0 text-lg font-semibold leading-6 tracking-tight">{copy.headline}</p>
        <p className="m-0 text-sm font-normal leading-5">{copy.detail}</p>
      </div>
    </div>
  );
};
